import type { LocationQuery } from 'vue-router';
import { CurrentHeaderFilter } from '@/models/global/currentHeaderFilter';
import { JST_URL_ROUTES } from '@/config/jstUrlRoutes';
import { snakeCases } from '@/utils/case_style';

/**
 * 現在の URL クエリ (Vue Router の LocationQuery) をそのまま引き継いだ
 * リンク文字列を組み立てる。
 *
 * 期間 (start_time / end_time / rel_time) や global header filter
 * (organization 等) を画面遷移をまたいで維持するために使う。
 * これらは currentHeaderFilter (global provide) と URL の二系統で
 * 状態を持っているため、リンク経由で URL を落とすと両者がズレて
 * カレンダー表示とコストが食い違う原因になる。
 */
export const buildLinkPreservingQuery = (
  path: string,
  query: LocationQuery
): string => {
  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== null && v !== undefined) {
          searchParams.append(key, String(v));
        }
      });
    } else {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};

/**
 * 値がundefined/null/空文字かどうかを判定する
 */
export const isEmptyValue = (value: unknown): boolean => {
  return value === undefined || value === null || value === '';
};

/**
 * オブジェクトからundefined/null/空文字のプロパティを除外する
 * Vue Routerのrouter.push({ query })等で使用
 */
export const omitEmptyValues = <T extends Record<string, unknown>>(
  obj: T
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => !isEmptyValue(v))
  ) as Partial<T>;
};

// JST 形式で URL に日時を載せるべきルート。
// src/config/jstUrlRoutes.ts の JST_URL_ROUTES を参照
// AWS 請求 (UTC) と整合させるため `snakeCases` の UTC Z 変換を迂回する。
const isJstUrlRoute = (basePath: string): boolean =>
  JST_URL_ROUTES.some(
    (p) =>
      basePath === p ||
      basePath.startsWith(`${p}?`) ||
      basePath.startsWith(`${p}/`)
  );

export const urlWithGlobalParams = (
  basePath: string,
  currentHeaderFilter: CurrentHeaderFilter
) => {
  const jstUrlRoute = isJstUrlRoute(basePath);
  const params = {
    organization: currentHeaderFilter.organization,
    // JST URL ルートでは DateWithZone を user TZ の ISO 文字列に先に変換して渡す
    // (string になるので snakeCases の DateWithZone -> UTC 変換を通らない)
    startTime: jstUrlRoute
      ? currentHeaderFilter.startTime?.model.toISO()
      : currentHeaderFilter.startTime,
    endTime: jstUrlRoute
      ? currentHeaderFilter.endTime?.model.toISO()
      : currentHeaderFilter.endTime,
    relTime: currentHeaderFilter.relTime,
  };
  const queries = snakeCases({
    params,
  });
  const query_strings = [];
  if (queries.params.organization !== undefined) {
    query_strings.push(
      `organization=${encodeURIComponent(queries.params.organization)}`
    );
  }
  if (queries.params.rel_time !== undefined) {
    query_strings.push(
      `rel_time=${encodeURIComponent(queries.params.rel_time)}`
    );
  } else {
    if (queries.params.start_time !== undefined) {
      query_strings.push(
        `start_time=${encodeURIComponent(queries.params.start_time)}`
      );
    }
    if (queries.params.end_time !== undefined) {
      query_strings.push(
        `end_time=${encodeURIComponent(queries.params.end_time)}`
      );
    }
  }
  if (query_strings.length > 0) {
    return `${basePath}?${query_strings.join('&')}`;
  }
  return basePath;
};

export const buildMenuUrl = (
  menuPath: string,
  currentRoute: { path: string; fullPath: string },
  currentHeaderFilter: CurrentHeaderFilter
) => {
  if (currentRoute.path === menuPath) {
    return currentRoute.fullPath;
  }
  return urlWithGlobalParams(menuPath, currentHeaderFilter);
};

// テキストからリンクを探し、実際のリンクに変換
export const linkify = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
};
